const FIREBASE_FUNCTIONS_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL;

// Customer Booking Error Codes (mirror from backend)
export const CustomerBookingErrorCodes = {
  // Authentication & Authorization
  PHONE_MISMATCH: 'CUSTOMER_PHONE_MISMATCH',
  UNAUTHORIZED: 'CUSTOMER_UNAUTHORIZED',
  INVALID_TOKEN: 'CUSTOMER_INVALID_TOKEN',

  // Customer lookup and registration
  CUSTOMER_NOT_FOUND: 'CUSTOMER_NOT_FOUND',
  UNREGISTERED_BOOKING_DISABLED: 'UNREGISTERED_BOOKING_DISABLED',
  FULL_NAME_REQUIRED: 'CUSTOMER_FULL_NAME_REQUIRED',
  INVALID_CUSTOMER_DATA: 'INVALID_CUSTOMER_DATA',

  // Appointment operations
  APPOINTMENT_ID_REQUIRED: 'APPOINTMENT_ID_REQUIRED',
  APPOINTMENT_DATA_REQUIRED: 'APPOINTMENT_DATA_REQUIRED',
  SERVICE_ID_REQUIRED: 'SERVICE_ID_REQUIRED',
  DATE_TIME_REQUIRED: 'DATE_TIME_REQUIRED',

  // Business context
  BUSINESS_NOT_FOUND: 'BUSINESS_NOT_FOUND',
  BUSINESS_CONTEXT_ERROR: 'BUSINESS_CONTEXT_ERROR',

  // Generic errors
  DATABASE_ERROR: 'CUSTOMER_DATABASE_ERROR',
  INTERNAL_ERROR: 'CUSTOMER_INTERNAL_ERROR',
  OPERATION_FAILED: 'CUSTOMER_OPERATION_FAILED'
};

export class CustomerBookingAPI {
  /**
   * Create auth headers with customer token
   */
  static createCustomerHeaders(customerToken) {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${customerToken}`
    };
  }

  /**
   * Create custom error with error code
   */
  static createError(message, errorCode = null, originalError = null) {
    const error = new Error(message);
    error.errorCode = errorCode;
    error.originalError = originalError;
    return error;
  }

  /**
   * Get customer's appointments
   * @param {string} businessSlug - Business slug
   * @param {string} customerToken - Customer verification token
   * @returns {Promise<Array>} Customer appointments
   */
  static async getCustomerAppointments(businessSlug, customerToken) {
    try {
      console.log('Customer API: Getting appointments', { businessSlug });

      const url = `${FIREBASE_FUNCTIONS_BASE_URL}/customerBooking/api/customer/${businessSlug}/appointments/list`;

      const response = await fetch(url, {
        method: 'POST',
        headers: this.createCustomerHeaders(customerToken)
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        const errorCode = result.errorCode || null;
        throw this.createError(
          result.error || 'Failed to get appointments',
          errorCode
        );
      }

      console.log('Customer API: Successfully retrieved appointments', {
        businessSlug,
        count: result.data?.length || 0
      });

      return result.data || [];

    } catch (error) {
      console.error('Customer API Error in getCustomerAppointments:', error);
      if (error.errorCode) {
        throw error;
      }
      throw this.createError(`Failed to get appointments: ${error.message}`);
    }
  }

  /**
   * Create new appointment
   * @param {string} businessSlug - Business slug
   * @param {Object} appointmentData - Appointment data
   * @param {string} customerToken - Customer verification token
   * @returns {Promise<Object>} Created appointment
   */
  static async createAppointment(businessSlug, appointmentData, customerToken) {
    try {
      console.log('Customer API: Creating appointment', { businessSlug, appointmentData });

      const url = `${FIREBASE_FUNCTIONS_BASE_URL}/customerBooking/api/customer/${businessSlug}/appointments/create`;

      const response = await fetch(url, {
        method: 'POST',
        headers: this.createCustomerHeaders(customerToken),
        body: JSON.stringify({ appointmentData })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        const errorCode = result.errorCode || null;
        throw this.createError(
          result.error || 'Failed to create appointment',
          errorCode
        );
      }

      console.log('Customer API: Successfully created appointment', {
        businessSlug,
        appointmentId: result.data?.id
      });

      return result.data;

    } catch (error) {
      console.error('Customer API Error in createAppointment:', error);
      if (error.errorCode) {
        throw error;
      }
      throw this.createError(`Failed to create appointment: ${error.message}`);
    }
  }

  /**
   * Cancel appointment
   * @param {string} businessSlug - Business slug
   * @param {string} appointmentId - Appointment ID to cancel
   * @param {string} customerToken - Customer verification token
   * @returns {Promise<Object>} Cancellation result
   */
  static async cancelAppointment(businessSlug, appointmentId, customerToken) {
    try {
      console.log('Customer API: Cancelling appointment', { businessSlug, appointmentId });

      const url = `${FIREBASE_FUNCTIONS_BASE_URL}/customerBooking/api/customer/${businessSlug}/appointments/cancel`;

      const response = await fetch(url, {
        method: 'POST',
        headers: this.createCustomerHeaders(customerToken),
        body: JSON.stringify({ appointmentId })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        const errorCode = result.errorCode || null;
        throw this.createError(
          result.error || 'Failed to cancel appointment',
          errorCode
        );
      }

      console.log('Customer API: Successfully cancelled appointment', {
        businessSlug,
        appointmentId
      });

      return result.data;

    } catch (error) {
      console.error('Customer API Error in cancelAppointment:', error);
      if (error.errorCode) {
        throw error;
      }
      throw this.createError(`Failed to cancel appointment: ${error.message}`);
    }
  }

  static async getCustomerServices(businessSlug, customerToken) {
    try {
      const url = `${FIREBASE_FUNCTIONS_BASE_URL}/customerBooking/api/customer/${businessSlug}/services`;

      const response = await fetch(url, {
        method: 'POST',
        headers: this.createCustomerHeaders(customerToken)
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        const errorCode = result.errorCode || null;
        throw this.createError(
          result.error || 'Failed to get services',
          errorCode
        );
      }

      return result.data || [];

    } catch (error) {
      if (error.errorCode) {
        throw error;
      }
      throw this.createError(`Failed to get services: ${error.message}`);
    }
  }

  // ADD after getCustomerServices method
  static async getAvailableSlots(businessSlug, customerToken, serviceId, startDate, endDate, options = {}) {
    try {
      const url = `${FIREBASE_FUNCTIONS_BASE_URL}/customerBooking/api/customer/${businessSlug}/slots`;

      const response = await fetch(url, {
        method: 'POST',
        headers: this.createCustomerHeaders(customerToken),
        body: JSON.stringify({
          serviceId,
          startDate,
          endDate,
          options
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        const errorCode = result.errorCode || null;
        throw this.createError(
          result.error || 'Failed to get available slots',
          errorCode
        );
      }

      return result.data || [];

    } catch (error) {
      if (error.errorCode) {
        throw error;
      }
      throw this.createError(`Failed to get available slots: ${error.message}`);
    }
  }

static async lookupCustomerByPhone(businessSlug, phone, verificationToken, customerDetails = null) {
  try {
    console.log('Customer API: Looking up customer by phone', {
      businessSlug,
      phone: phone ? phone.substring(0, 3) + '***' : 'undefined',
      hasCustomerDetails: !!customerDetails
    });

    const url = `${FIREBASE_FUNCTIONS_BASE_URL}/customerBooking/api/customer/${businessSlug}/customer/lookup`;

    const requestBody = { phone };

    // Handle both legacy string parameter and new object parameter
    if (customerDetails) {
      if (typeof customerDetails === 'string') {
        // Legacy: customerDetails is just fullName string
        requestBody.fullName = customerDetails.trim();
      } else if (typeof customerDetails === 'object') {
        // New: customerDetails is an object with multiple fields
        if (customerDetails.customerFullName && customerDetails.customerFullName.trim()) {
          requestBody.fullName = customerDetails.customerFullName.trim();
        }
        if (customerDetails.email && customerDetails.email.trim()) {
          requestBody.email = customerDetails.email.trim();
        }
        if (customerDetails.birth_date) {
          requestBody.birth_date = customerDetails.birth_date;
        }
        if (customerDetails.gender) {
          requestBody.gender = customerDetails.gender;
        }
      }
    }

    console.log('Customer API: Request payload', {
      phone: phone ? phone.substring(0, 3) + '***' : 'undefined',
      hasFullName: !!requestBody.fullName,
      hasEmail: !!requestBody.email,
      hasBirthDate: !!requestBody.birth_date,
      hasGender: !!requestBody.gender
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: this.createCustomerHeaders(verificationToken),
      body: JSON.stringify(requestBody)
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      const errorCode = result.errorCode || null;

      // Handle customer not found specially
      if (errorCode === CustomerBookingErrorCodes.CUSTOMER_NOT_FOUND || response.status === 404) {
        console.log('Customer API: Customer not found', { businessSlug });
        return null;
      }

      throw this.createError(
        result.error || 'Failed to lookup customer',
        errorCode
      );
    }

    console.log('Customer API: Successfully found/created customer', {
      businessSlug,
      customerId: result.data?.id ? result.data.id.substring(0, 8) + '***' : 'undefined'
    });

    // Add booking token to customer data
    const customerWithToken = {
      ...result.data,
      token: verificationToken
    };

    return customerWithToken;

  } catch (error) {
    console.error('Customer API Error in lookupCustomerByPhone:', error);
    if (error.errorCode) {
      throw error;
    }
    throw this.createError(`Failed to lookup customer: ${error.message}`);
  }
}
}